import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaPlus, FaTrash, FaEdit, FaDownload, FaSearch, FaWpforms,
  FaListAlt, FaCopy, FaQrcode, FaUpload, FaExchangeAlt
} from 'react-icons/fa';

interface SubmissionItem {
  id: number;
  form_type: string;
  submission_data: Record<string, any>;
  verification_status?: string;
  submitted_at: string;
}

interface DynamicField {
  id: number;
  form_type: string;
  field_label: string;
  field_name: string;
  field_type: string;
  options?: string;
  is_required: number;
  sort_order: number;
}

const GetInTouch: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'SUBMISSIONS' | 'BUILDER'>('SUBMISSIONS');
  const [selectedFormType, setSelectedFormType] = useState<'ALL' | 'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST'>('ALL');

  const [submissions, setSubmissions] = useState<SubmissionItem[]>([]);
  const [fields, setFields] = useState<DynamicField[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [previewImageModal, setPreviewImageModal] = useState<string | null>(null);

  const isImageField = (key: string, val: any): boolean => {
    if (typeof val !== 'string') return false;
    const str = val.trim();
    const k = key.toLowerCase();
    return str.startsWith('data:image/') || str.startsWith('http://') || str.startsWith('https://') || k.includes('payment') || k.includes('receipt') || k.includes('screenshot') || k.includes('file') || k.includes('image') || k.includes('photo') || k.includes('qr');
  };
  const [builderFormType, setBuilderFormType] = useState<'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST'>('VISITOR_REGISTRATION');
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);

  const [newField, setNewField] = useState({
    field_label: '',
    field_type: 'text',
    optionsStr: '',
    qrImageUrl: '',
    qrAmount: '600',
    is_required: true
  });

  const [copyFormConfig, setCopyFormConfig] = useState({
    source_form_type: 'VISITOR_REGISTRATION' as 'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST',
    target_form_type: 'EXPRESS_INTEREST' as 'VISITOR_REGISTRATION' | 'EXPRESS_INTEREST',
    overwrite: false
  });

  const [isUploadingQr, setIsUploadingQr] = useState(false);

  useEffect(() => {
    fetchSubmissions();
    fetchFields(builderFormType);
  }, [selectedFormType, builderFormType]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submissions?form_type=${selectedFormType}`);
      if (res.data.success) {
        setSubmissions(res.data.submissions || []);
      }
    } catch (err) {
      console.error('Error fetching form submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendVisitorWhatsApp = (phone: string, name: string, status: string, eventTitle?: string) => {
    let p = (phone || '').replace(/\D/g, '');
    if (!p) {
      alert('No mobile number available for this visitor.');
      return;
    }
    if (p.length === 10) p = '91' + p;

    const isApproved = status === 'VERIFIED';
    const msg = isApproved
      ? `Hello ${name}! 🎉 Your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''} has been VERIFIED & APPROVED by RMB Admin. We look forward to welcoming you! - RMB Jaipur Chapter`
      : `Hello ${name}. Update regarding your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''}: Your registration status has been updated to REJECTED. Please contact RMB Admin for any queries. - RMB Jaipur Chapter`;

    const url = `https://api.whatsapp.com/send?phone=${p}&text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  const sendVisitorEmail = (email: string, name: string, status: string, eventTitle?: string) => {
    if (!email || !email.includes('@')) {
      alert('No valid email address available for this visitor.');
      return;
    }

    const isApproved = status === 'VERIFIED';
    const subj = isApproved
      ? `✅ Visitor Registration Approved - ${eventTitle || 'RMB Chapter Meeting'}`
      : `Update: Visitor Registration Status - ${eventTitle || 'RMB Chapter Meeting'}`;

    const body = isApproved
      ? `Dear ${name},\n\nWe are pleased to inform you that your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''} has been VERIFIED & APPROVED by the RMB Chapter Admin.\n\nStatus: APPROVED & CONFIRMED\n\nWe look forward to meeting you!\n\nBest regards,\nRMB Jaipur Chapter`
      : `Dear ${name},\n\nThis is an update regarding your visitor registration ${eventTitle ? `for "${eventTitle}"` : ''}. Your registration status has been updated to REJECTED.\n\nIf you have any questions, please contact RMB Admin.\n\nBest regards,\nRMB Jaipur Chapter`;

    const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const handleVerifySubmission = async (sub: any, status: 'VERIFIED' | 'REJECTED') => {
    const data = sub.submission_data || {};
    const vName = data.visitor_name || data.full_name || data.name || 'Visitor';
    const vPhone = data.visitor_phone || data.mobile || data.phone || '';
    const eventTitle = data.event_title || data.event_name || '';

    const actionText = status === 'VERIFIED' ? 'accept & verify' : 'reject';
    if (!window.confirm(`Are you sure you want to ${actionText} the registration submission for "${vName}"?`)) {
      return;
    }

    try {
      const res = await axios.put(`https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/submissions/${sub.id}/verify`, {
        verification_status: status
      });
      if (res.data.success) {
        alert(`✅ Submission transaction marked as ${status === 'VERIFIED' ? 'ACCEPTED & VERIFIED' : 'REJECTED'}!`);
        fetchSubmissions();

        if (vPhone && window.confirm(`📱 Would you like to send a WhatsApp status update message to ${vName} (${vPhone}) now?`)) {
          sendVisitorWhatsApp(vPhone, vName, status, eventTitle);
        }
      }
    } catch (err) {
      console.error('Error verifying submission:', err);
      alert('Failed to update submission verification status.');
    }
  };

  const fetchFields = async (type: string) => {
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields?form_type=${type}`);
      if (res.data.success) {
        setFields(res.data.fields || []);
      }
    } catch (err) {
      console.error('Error fetching fields:', err);
    }
  };

  const handleQrPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setIsUploadingQr(true);

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedPath = `https://rmbbackend.rmbjaipur.com/${res.data.imagePath.replace(/\\/g, '/')}`;
      setNewField(prev => ({ ...prev, qrImageUrl: uploadedPath }));
      alert('📷 Custom QR Scanner Photo uploaded successfully!');
    } catch (err) {
      console.error('QR Photo upload failed:', err);
      alert('Failed to upload QR scanner photo.');
    } finally {
      setIsUploadingQr(false);
    }
  };

  const handleAddField = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let parsedOpts: any = null;
      if (newField.field_type === 'select' && newField.optionsStr) {
        parsedOpts = newField.optionsStr.split(',').map(s => s.trim()).filter(Boolean);
      } else if (newField.field_type === 'qr_payment') {
        parsedOpts = {
          qr_image: newField.qrImageUrl,
          amount: newField.qrAmount
        };
      }

      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields', {
        form_type: builderFormType,
        field_label: newField.field_label,
        field_type: newField.field_type,
        options: parsedOpts,
        is_required: newField.is_required ? 1 : 0,
        sort_order: fields.length + 1
      });

      if (res.data.success) {
        alert('✨ Dynamic field saved successfully!');
        setIsAddFieldModalOpen(false);
        setNewField({ field_label: '', field_type: 'text', optionsStr: '', qrImageUrl: '', qrAmount: '600', is_required: true });
        fetchFields(builderFormType);
      }
    } catch (err) {
      console.error('Error adding dynamic field:', err);
      alert('Failed to save field.');
    }
  };

  const handleDeleteField = async (id: number) => {
    if (!window.confirm('Are you sure you want to remove this field?')) return;
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/fields/${id}`);
      fetchFields(builderFormType);
    } catch (err) {
      console.error('Error deleting field:', err);
    }
  };

  const handleCopyFields = async (e: React.FormEvent) => {
    e.preventDefault();
    if (copyFormConfig.source_form_type === copyFormConfig.target_form_type) {
      alert('Source and Target form cannot be the same!');
      return;
    }

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/dynamic-forms/copy-fields', copyFormConfig);
      if (res.data.success) {
        alert(`📋 ${res.data.message}`);
        setIsCopyModalOpen(false);
        fetchFields(builderFormType);
      }
    } catch (err) {
      console.error('Error copying form fields:', err);
      alert('Failed to copy form fields.');
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const jsonStr = JSON.stringify(sub.submission_data || {}).toLowerCase();
    return jsonStr.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="p-6 bg-[#131E32] min-h-screen text-slate-100 space-y-6">

      {/* Header Banner */}
      <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-xl">
        <div>
          <h1 className="text-xl font-black text-white flex items-center gap-2">
            <FaWpforms className="text-[#FDBC12]" /> Dynamic Form Submissions & Custom Field Builder
          </h1>
          <p className="text-xs text-slate-400">
            Upload custom payment QR scanner photos, copy fields between forms, and view website submissions.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex bg-[#131E32] p-1.5 rounded-xl border border-[#2A3B5C] text-xs font-bold">
          <button
            onClick={() => setActiveTab('SUBMISSIONS')}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${activeTab === 'SUBMISSIONS'
                ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow'
                : 'text-slate-300 hover:text-white'
              }`}
          >
            <FaListAlt /> Form Submissions ({submissions.length})
          </button>
          <button
            onClick={() => setActiveTab('BUILDER')}
            className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${activeTab === 'BUILDER'
                ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow'
                : 'text-slate-300 hover:text-white'
              }`}
          >
            <FaWpforms /> Customize Form Fields
          </button>
        </div>
      </div>

      {/* 🔗 Shareable Visitor Registration Link Banner */}
      <div className="bg-gradient-to-r from-[#0A2451] via-[#02256D] to-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-cyan-500/40 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-[#FDBC12]/10 border border-[#FDBC12]/30 flex items-center justify-center text-[#FDBC12] text-xl font-black flex-shrink-0">
            🎟️
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/30">
                Shareable Visitor Link
              </span>
              <span className="text-xs text-slate-300 font-mono hidden md:inline">
                {window.location.origin}/visitor-register
              </span>
            </div>
            <h3 className="text-sm font-black text-white mt-1">
              Public Visitor Meeting Registration Form URL
            </h3>
            <p className="text-xs text-slate-300">
              Share this registration link on WhatsApp or Email with guest business owners. Submissions connect live to this builder.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 w-full sm:w-auto">
          <a
            href="/visitor-register"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 sm:flex-none px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl border border-slate-700 transition flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <span>👁️ Open Visitor Form</span>
          </a>
          <button
            onClick={() => {
              const url = `${window.location.origin}/visitor-register`;
              navigator.clipboard.writeText(url);
              alert('🎉 Visitor Registration Link copied to clipboard!\n\nShareable URL: ' + url);
            }}
            className="flex-1 sm:flex-none px-5 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg transition flex items-center justify-center gap-1.5 whitespace-nowrap cursor-pointer"
          >
            <span>📋 Copy Visitor Link</span>
          </button>
        </div>
      </div>

      {/* TAB 1: FORM SUBMISSIONS VIEWER */}
      {activeTab === 'SUBMISSIONS' && (
        <div className="space-y-4">
          <div className="bg-[#1E2B45] p-4 rounded-xl border border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="relative w-full sm:w-80">
              <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-[#131E32] border border-[#2A3B5C] focus:border-[#FDBC12] rounded-lg text-xs text-white outline-none font-bold"
              />
            </div>

            <div className="flex items-center gap-2 text-xs font-bold">
              <span className="text-slate-400">Filter Form:</span>
              {(['ALL', 'VISITOR_REGISTRATION', 'EXPRESS_INTEREST'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setSelectedFormType(t)}
                  className={`px-3 py-1.5 rounded-lg transition ${selectedFormType === t
                      ? 'bg-[#FDBC12] text-[#0A2451] font-black shadow'
                      : 'bg-[#131E32] text-slate-300 border border-[#2A3B5C]'
                    }`}
                >
                  {t === 'ALL' ? 'All Submissions' : t === 'VISITOR_REGISTRATION' ? '🎟️ Visitor Registrations' : '🤝 Express Interest'}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-[#0A2451] text-slate-300 font-extrabold uppercase border-b border-[#2A3B5C]">
                    <th className="p-3">#</th>
                    <th className="p-3">Form Type</th>
                    <th className="p-3">Submitted Details</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Submitted Date</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A3B5C]">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400">Loading form submissions...</td>
                    </tr>
                  ) : filteredSubmissions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400">No submissions found matching criteria.</td>
                    </tr>
                  ) : (
                    filteredSubmissions.map((sub, idx) => {
                      const isVerified = sub.verification_status === 'VERIFIED';
                      const isRejected = sub.verification_status === 'REJECTED';

                      return (
                        <tr key={sub.id} className="hover:bg-[#131E32] transition">
                          <td className="p-3 font-bold text-slate-400">{idx + 1}</td>
                          <td className="p-3">
                            <span className={`px-2.5 py-1 rounded-full font-black text-[10px] uppercase ${sub.form_type === 'VISITOR_REGISTRATION'
                                ? 'bg-rose-950 text-rose-300 border border-rose-500/40'
                                : 'bg-blue-950 text-blue-300 border border-blue-500/40'
                              }`}>
                              {sub.form_type === 'VISITOR_REGISTRATION' ? '🎟️ Visitor Registration' : '🤝 Express Interest'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] bg-[#131E32] p-3 rounded-xl border border-white/5">
                              {Object.entries(sub.submission_data || {}).map(([k, v]) => {
                                const isImg = isImageField(k, v);
                                if (isImg) {
                                  return (
                                    <div key={k} className="col-span-full bg-[#0A2451] p-2.5 rounded-xl border border-cyan-500/30 flex items-center justify-between gap-3">
                                      <div>
                                        <span className="text-cyan-300 font-extrabold capitalize text-[10px] block">📷 {k.replace(/_/g, ' ')}</span>
                                        <span className="text-slate-400 text-[10px]">Click image to expand payment receipt screenshot</span>
                                      </div>
                                      <img
                                        src={String(v)}
                                        alt="Uploaded Payment Receipt"
                                        onClick={() => setPreviewImageModal(String(v))}
                                        className="w-14 h-14 object-cover rounded-lg border border-cyan-400/50 cursor-pointer hover:scale-105 transition shadow-lg"
                                      />
                                    </div>
                                  );
                                }
                                return (
                                  <div key={k} className="overflow-hidden">
                                    <span className="text-slate-400 font-bold capitalize">{k.replace(/_/g, ' ')}: </span>
                                    <span className="text-white font-semibold">
                                      {typeof v === 'boolean' ? (v ? '✅ Yes' : '❌ No') : String(v || '-')}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full font-black text-[10px] uppercase ${isVerified ? 'bg-emerald-950 text-emerald-300 border border-emerald-500/40' :
                                isRejected ? 'bg-rose-950 text-rose-300 border border-rose-500/40' :
                                  'bg-amber-950 text-amber-300 border border-amber-500/40 animate-pulse'
                              }`}>
                              {isVerified ? '🟢 Verified' : isRejected ? '🔴 Rejected' : '🟡 Verification Pending'}
                            </span>
                          </td>
                          <td className="p-3 text-slate-400 font-semibold whitespace-nowrap">
                            {new Date(sub.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            {(() => {
                              const data = sub.submission_data || {};
                              const vName = data.visitor_name || data.full_name || data.name || 'Visitor';
                              const vPhone = data.visitor_phone || data.mobile || data.phone || '';
                              const vEmail = data.visitor_email || data.email || '';
                              const eventTitle = data.event_title || data.event_name || '';

                              return (
                                <div className="flex items-center justify-end gap-1.5">
                                  {!isVerified && (
                                    <button
                                      onClick={() => handleVerifySubmission(sub, 'VERIFIED')}
                                      className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[11px] rounded-lg shadow transition cursor-pointer"
                                    >
                                      ✅ Accept
                                    </button>
                                  )}
                                  {!isRejected && (
                                    <button
                                      onClick={() => handleVerifySubmission(sub, 'REJECTED')}
                                      className="px-2.5 py-1 bg-rose-600/30 hover:bg-rose-600 text-rose-300 hover:text-white font-bold text-[11px] rounded-lg border border-rose-500/40 transition cursor-pointer"
                                    >
                                      ❌ Reject
                                    </button>
                                  )}

                                  {vPhone && (
                                    <button
                                      onClick={() => sendVisitorWhatsApp(vPhone, vName, sub.verification_status || 'VERIFIED', eventTitle)}
                                      className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-600 text-emerald-300 hover:text-white rounded-lg border border-emerald-500/40 font-bold text-[10px] transition cursor-pointer flex items-center gap-1"
                                      title={`Send WhatsApp Message to ${vName} (${vPhone})`}
                                    >
                                      💬 WhatsApp
                                    </button>
                                  )}

                                  {vEmail && (
                                    <button
                                      onClick={() => sendVisitorEmail(vEmail, vName, sub.verification_status || 'VERIFIED', eventTitle)}
                                      className="px-2 py-1 bg-cyan-500/20 hover:bg-cyan-600 text-cyan-300 hover:text-white rounded-lg border border-cyan-500/40 font-bold text-[10px] transition cursor-pointer flex items-center gap-1"
                                      title={`Send Email Notification to ${vName} (${vEmail})`}
                                    >
                                      ✉️ Email
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: DYNAMIC FORM FIELD BUILDER */}
      {activeTab === 'BUILDER' && (
        <div className="space-y-4">
          <div className="bg-[#1E2B45] p-5 rounded-2xl border border-[#2A3B5C] flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-slate-400">Form to Customize:</span>
              <button
                onClick={() => setBuilderFormType('VISITOR_REGISTRATION')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition ${builderFormType === 'VISITOR_REGISTRATION'
                    ? 'bg-rose-600 text-white shadow'
                    : 'bg-[#131E32] text-slate-300 border border-[#2A3B5C]'
                  }`}
              >
                🎟️ Visitor Registration Form
              </button>
              <button
                onClick={() => setBuilderFormType('EXPRESS_INTEREST')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition ${builderFormType === 'EXPRESS_INTEREST'
                    ? 'bg-[#02256D] text-white shadow'
                    : 'bg-[#131E32] text-slate-300 border border-[#2A3B5C]'
                  }`}
              >
                🤝 Express Interest Form
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setCopyFormConfig({
                    source_form_type: builderFormType,
                    target_form_type: builderFormType === 'VISITOR_REGISTRATION' ? 'EXPRESS_INTEREST' : 'VISITOR_REGISTRATION',
                    overwrite: false
                  });
                  setIsCopyModalOpen(true);
                }}
                className="px-4 py-2.5 bg-[#02256D] hover:bg-blue-900 text-white font-bold text-xs rounded-xl shadow flex items-center gap-2"
              >
                <FaCopy /> Copy Fields to Other Form
              </button>

              <button
                onClick={() => setIsAddFieldModalOpen(true)}
                className="px-4 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow flex items-center gap-2"
              >
                <FaPlus /> Add Custom Field
              </button>
            </div>
          </div>

          <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] overflow-hidden p-5 shadow-xl space-y-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-2">
              Active Fields ({fields.length}) - {builderFormType === 'VISITOR_REGISTRATION' ? 'Visitor Registration Form' : 'Express Interest Form'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((f, i) => (
                <div key={f.id} className="bg-[#131E32] border border-[#2A3B5C] rounded-xl p-4 flex justify-between items-center">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="bg-[#0A2451] text-[#FDBC12] font-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                        {i + 1}
                      </span>
                      <h4 className="font-bold text-white text-xs">{f.field_label}</h4>
                      {f.is_required ? <span className="text-red-400 text-[10px] font-bold">(Required)</span> : <span className="text-slate-500 text-[10px]">(Optional)</span>}
                    </div>
                    <p className="text-[11px] text-slate-400">
                      Type: <strong className="text-amber-400 uppercase">{f.field_type}</strong> | Variable: <code className="bg-black/40 px-1.5 py-0.5 rounded text-pink-300">{f.field_name}</code>
                    </p>
                  </div>

                  <button
                    onClick={() => handleDeleteField(f.id)}
                    className="p-2 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded-lg text-xs transition"
                    title="Remove Field"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ADD FIELD MODAL WITH QR PHOTOGRAPH UPLOAD */}
      {isAddFieldModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#1E2B45] border border-[#2A3B5C] rounded-2xl max-w-md w-full p-6 text-slate-100 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-[#2A3B5C] pb-3">
              <h3 className="font-bold text-white text-sm">✨ Add Custom Field to {builderFormType}</h3>
              <button onClick={() => setIsAddFieldModalOpen(false)} className="text-slate-400 hover:text-white font-bold text-xs">✕</button>
            </div>

            <form onSubmit={handleAddField} className="space-y-3 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Field Label *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Registration Fee QR Payment / Preferred Location"
                  value={newField.field_label}
                  onChange={(e) => setNewField({ ...newField, field_label: e.target.value })}
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Field Input Type *</label>
                <select
                  value={newField.field_type}
                  onChange={(e) => setNewField({ ...newField, field_type: e.target.value })}
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                >
                  <option value="text">Single Line Text</option>
                  <option value="email">Email Address</option>
                  <option value="tel">Mobile / Phone Number</option>
                  <option value="number">Numeric Input</option>
                  <option value="date">Date Picker</option>
                  <option value="textarea">Multi-line Text Area</option>
                  <option value="select">Dropdown Options Select</option>
                  <option value="file">File / Photo Upload</option>
                  <option value="qr_payment">📱 QR Code Payment Scanner Photograph</option>
                </select>
              </div>

              {newField.field_type === 'qr_payment' && (
                <div className="bg-[#131E32] p-3 rounded-xl border border-amber-500/40 space-y-3">
                  <div>
                    <label className="font-bold text-amber-300 block mb-1">Collectible Amount (₹)</label>
                    <input
                      type="number"
                      value={newField.qrAmount}
                      onChange={(e) => setNewField({ ...newField, qrAmount: e.target.value })}
                      placeholder="e.g. 600"
                      className="w-full p-2 bg-[#1E2B45] border border-[#2A3B5C] rounded text-white font-bold"
                    />
                  </div>

                  <div>
                    <label className="font-bold text-amber-300 block mb-1">Upload QR Scanner Photograph *</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleQrPhotoUpload}
                      className="w-full text-xs text-slate-300 file:mr-2 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-[#02256D] file:text-white font-bold"
                    />
                    {isUploadingQr && <p className="text-amber-400 font-bold text-[11px] animate-pulse mt-1">Uploading QR Photo...</p>}
                    {newField.qrImageUrl && (
                      <div className="mt-2 text-center">
                        <img src={newField.qrImageUrl} alt="QR Scanner Preview" className="w-28 h-28 object-contain mx-auto rounded border-2 border-amber-400 bg-white p-1" />
                        <p className="text-[10px] text-emerald-400 font-bold mt-1">✅ Custom QR Scanner Attached</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {newField.field_type === 'select' && (
                <div>
                  <label className="font-bold text-slate-300 block mb-1">Dropdown Options (Comma separated) *</label>
                  <input
                    type="text"
                    required
                    placeholder="Option 1, Option 2, Option 3"
                    value={newField.optionsStr}
                    onChange={(e) => setNewField({ ...newField, optionsStr: e.target.value })}
                    className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white outline-none"
                  />
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="req_toggle"
                  checked={newField.is_required}
                  onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                  className="w-4 h-4 text-[#FDBC12] rounded"
                />
                <label htmlFor="req_toggle" className="font-bold text-slate-200 cursor-pointer">Required Field</label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setIsAddFieldModalOpen(false)}
                  className="px-4 py-2 bg-[#131E32] text-slate-300 rounded-lg font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#FDBC12] text-[#0A2451] font-black rounded-lg shadow"
                >
                  Save Field
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* COPY FORM FIELDS MODAL */}
      {isCopyModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#1E2B45] border border-[#2A3B5C] rounded-2xl max-w-md w-full p-6 text-slate-100 shadow-2xl space-y-4">
            <div className="flex justify-between items-center border-b border-[#2A3B5C] pb-3">
              <h3 className="font-bold text-white text-sm flex items-center gap-2">
                <FaCopy className="text-[#FDBC12]" /> Copy Dynamic Fields Between Forms
              </h3>
              <button onClick={() => setIsCopyModalOpen(false)} className="text-slate-400 hover:text-white font-bold text-xs">✕</button>
            </div>

            <form onSubmit={handleCopyFields} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-300 block mb-1">Source Form (Copy From) *</label>
                <select
                  value={copyFormConfig.source_form_type}
                  onChange={(e) => setCopyFormConfig({ ...copyFormConfig, source_form_type: e.target.value as any })}
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                >
                  <option value="VISITOR_REGISTRATION">🎟️ Visitor Registration Form</option>
                  <option value="EXPRESS_INTEREST">🤝 Express Interest Form</option>
                </select>
              </div>

              <div className="text-center text-slate-400 font-bold">
                <FaExchangeAlt className="mx-auto text-amber-400 text-lg" />
              </div>

              <div>
                <label className="font-bold text-slate-300 block mb-1">Target Form (Copy To) *</label>
                <select
                  value={copyFormConfig.target_form_type}
                  onChange={(e) => setCopyFormConfig({ ...copyFormConfig, target_form_type: e.target.value as any })}
                  className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white font-bold outline-none"
                >
                  <option value="EXPRESS_INTEREST">🤝 Express Interest Form</option>
                  <option value="VISITOR_REGISTRATION">🎟️ Visitor Registration Form</option>
                </select>
              </div>

              <div className="flex items-center gap-2 bg-[#131E32] p-3 rounded-lg border border-[#2A3B5C]">
                <input
                  type="checkbox"
                  id="overwrite_toggle"
                  checked={copyFormConfig.overwrite}
                  onChange={(e) => setCopyFormConfig({ ...copyFormConfig, overwrite: e.target.checked })}
                  className="w-4 h-4 text-[#FDBC12] rounded"
                />
                <label htmlFor="overwrite_toggle" className="font-bold text-slate-200 cursor-pointer">
                  Overwrite existing fields in target form (If unchecked, fields will be appended)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-[#2A3B5C]">
                <button
                  type="button"
                  onClick={() => setIsCopyModalOpen(false)}
                  className="px-4 py-2 bg-[#131E32] text-slate-300 rounded-lg font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-[#FDBC12] text-[#0A2451] font-black rounded-lg shadow"
                >
                  Confirm & Copy Fields
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FULL PAYMENT RECEIPT IMAGE LIGHTBOX MODAL */}
      {previewImageModal && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setPreviewImageModal(null)}>
          <div className="relative max-w-3xl w-full max-h-[90vh] bg-[#1E2B45] border border-cyan-500/40 rounded-2xl overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0A2451] p-4 border-b border-[#2A3B5C] flex justify-between items-center text-white font-bold text-xs">
              <span className="flex items-center gap-2 text-cyan-300">📷 Uploaded Payment Receipt / Screenshot Preview</span>
              <button
                onClick={() => setPreviewImageModal(null)}
                className="w-7 h-7 bg-rose-600 hover:bg-rose-700 text-white rounded-full flex items-center justify-center font-bold"
              >
                ✕
              </button>
            </div>
            <div className="p-4 flex-1 flex justify-center items-center overflow-auto bg-black/60">
              <img src={previewImageModal} alt="Payment Receipt Screenshot" className="max-h-[75vh] w-auto object-contain rounded-lg shadow-2xl border border-white/10" />
            </div>
            <div className="p-3 bg-[#0A2451] border-t border-[#2A3B5C] flex justify-between items-center text-xs">
              <a href={previewImageModal} download="payment_receipt.png" target="_blank" rel="noreferrer" className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg shadow flex items-center gap-2">
                📥 Download Original File
              </a>
              <button onClick={() => setPreviewImageModal(null)} className="px-4 py-2 bg-[#1E2B45] text-slate-300 rounded-lg font-bold">
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GetInTouch;
